import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { PagePaper } from "../Paper";
import { useApiClient } from "@/hooks/useApiClient";
import { useEffect, useRef, useState } from "react";
import { usePrefectures } from "@/hooks/usePrefectures";
import {
  getSelectNextLocation,
  type IAddressTree,
} from "@/api/getSelectNextLocation";
import { ButtonColorOption, ButtonVariantOption } from "@/components/Button";
import { SizingWrapperStyle } from "../Wrapper";
import { type CityListResponse } from "@/types/city";
import { toast } from "react-toastify";

interface IProps {
  open: boolean;
  onClose: () => void;
  prefectureId: number;
  cityInfo?: {
    setCode: (cityCode: string) => void; // setCityはページから呼ばれた場合に設定され、モーダルから呼ばれた場合は設定されない
    listResponse: CityListResponse;
  };
  setLocationWithoutCity: (locationWithoutCity: string) => void;
  setInputTextDirectly?: React.Dispatch<React.SetStateAction<boolean>>; // setCityが設定されている場合は、setInputTextDirectlyも必須
}

const SELECT_CITY_COUNT = 2;

const DISPLAY_COLUMNS_COUNT = 4;
const FETCH_FAILED_MESSAGE =
  "所在の取得に失敗しました。時間をおいて再度お試しください。";
let resultCity = "";
// Cityを外したLocationと命名していますが、モーダルの場合はCityも含めた値を保持する
let resultLocationWithoutCity = "";

export const SelectLocationModal: React.FC<IProps> = (props) => {
  const {
    open,
    onClose,
    prefectureId,
    cityInfo,
    setLocationWithoutCity,
    setInputTextDirectly,
  } = props;

  const apiClient = useApiClient().apiClient;
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedLocations, setSelectedLocations] = useState<
    Array<{ value: string | null; label: string }>
  >([]);
  const [fetchLocationList, setFetchLocationList] = useState<IAddressTree[]>(
    []
  );
  const [displayLocationList, setDisplayLocationList] = useState<
    IAddressTree[][]
  >([]);
  const { prefectures } = usePrefectures();
  const [searchText, setSearchText] = useState<string>("");
  // 開く・閉じる・取得のたびに進める。閉じた後や次の操作の後に遅れて返った応答は、画面にも選択結果にも反映しない
  const generationRef = useRef(0);
  const startRequest = (): (() => boolean) => {
    generationRef.current += 1;
    const generation = generationRef.current;
    return () => generation === generationRef.current;
  };

  const handleClose = (): void => {
    generationRef.current += 1;
    onClose();
  };

  const handleClickLocationLabelInDialog: (
    item: IAddressTree
  ) => Promise<void> = async (item: IAddressTree) => {
    const isCurrent = startRequest();
    const previousCity = resultCity;
    const previousLocation = resultLocationWithoutCity;
    const previousSelectedLocations = selectedLocations;
    setLoading(true);
    setSearchText("");
    setSelectedLocations((prev) => [
      ...prev,
      { value: String(item.refId), label: item.name },
    ]);

    if (cityInfo) {
      if (!resultCity) {
        resultCity = item.name;
      } else {
        resultLocationWithoutCity = resultLocationWithoutCity + item.name;
      }
    } else {
      resultLocationWithoutCity = resultLocationWithoutCity + item.name;
    }

    const response = await getSelectNextLocation(
      apiClient,
      prefectureId,
      item.refId
    );
    if (!isCurrent()) return;
    // 取得に失敗したときは末端として確定せず、選ぶ前に戻す
    if (response.failed) {
      resultCity = previousCity;
      resultLocationWithoutCity = previousLocation;
      setSelectedLocations(previousSelectedLocations);
      toast.error(FETCH_FAILED_MESSAGE);
      setLoading(false);
      return;
    }
    if (response.list.length) {
      setFetchLocationList(response.list);
      setDisplayLocationList(splitData(response.list));
    } else {
      const removeSuffix = ["大字なし", "小字なし"];
      for (const suffix of removeSuffix) {
        if (resultLocationWithoutCity.endsWith(suffix)) {
          resultLocationWithoutCity = resultLocationWithoutCity.slice(
            0,
            -suffix.length
          );
        }
      }

      if (cityInfo && setInputTextDirectly) {
        const targetCity = cityInfo.listResponse.list.find(
          (city) => city.name === resultCity
        );
        if (targetCity) {
          cityInfo.setCode(targetCity.cityCode);
          setInputTextDirectly(true); // 市区町村が見つかればtrue
        } else {
          toast.info("取り扱っていない市区町村です。再度選択してください。");
          cityInfo.setCode("");
          resultLocationWithoutCity = "";
          setInputTextDirectly(false); // 市区町村が見つかればfalse
        }
      }
      setLocationWithoutCity(resultLocationWithoutCity);
      resultCity = "";
      resultLocationWithoutCity = "";
      setFetchLocationList([]);
      setDisplayLocationList([[]]);
      setPrefectureNameInSelectedLocations();
      onClose();
    }
    setLoading(false);
  };

  const splitData = (data: IAddressTree[]): IAddressTree[][] => {
    const chunked = [];
    for (let i = 0; i < data.length; i += DISPLAY_COLUMNS_COUNT) {
      chunked.push(data.slice(i, i + DISPLAY_COLUMNS_COUNT));
    }
    return chunked;
  };

  const handleBack: () => Promise<void> = async () => {
    // 取得中に続けて戻ると、捨てた応答の分だけ所在の切り詰めが漏れるため、取得が終わるまで受け付けない
    if (loading || selectedLocations.length < SELECT_CITY_COUNT) {
      return;
    }
    const isCurrent = startRequest();
    const previousCity = resultCity;
    const previousLocation = resultLocationWithoutCity;
    setLoading(true);
    setSearchText("");
    const newSelectedLocations = [...selectedLocations];
    newSelectedLocations.pop();
    setSelectedLocations(newSelectedLocations);
    if (newSelectedLocations.length < SELECT_CITY_COUNT && cityInfo) {
      resultCity = "";
      resultLocationWithoutCity = "";
    }
    // 外した階層の名前は応答を待たずに削る（応答を捨てた場合も、表示中の階層と所在がずれない）
    resultLocationWithoutCity = resultLocationWithoutCity.slice(
      0,
      -selectedLocations[selectedLocations.length - 1].label.length
    );
    // NOTE: newSelectedLocationsの長さが1の時とそうでない時でAPIのパスが変わる
    let response;
    if (newSelectedLocations.length < SELECT_CITY_COUNT) {
      response = await getSelectNextLocation(apiClient, prefectureId);
    } else {
      const refId =
        newSelectedLocations[newSelectedLocations.length - 1].value ??
        undefined;
      response = await getSelectNextLocation(
        apiClient,
        prefectureId,
        Number(refId)
      );
    }
    if (!isCurrent()) return;
    if (response.failed) {
      resultCity = previousCity;
      resultLocationWithoutCity = previousLocation;
      setSelectedLocations(selectedLocations);
      toast.error(FETCH_FAILED_MESSAGE);
      setLoading(false);
      return;
    }
    setFetchLocationList(response.list);
    setDisplayLocationList(splitData(response.list));
    setLoading(false);
  };

  const findPrefectureName = (): string =>
    prefectures.find(
      (prefecture) => Number(prefecture.prefCode) === prefectureId
    )?.name ?? "";

  // 「戻る」はパンくずの先頭が都道府県である前提で段数を数えるため、マスタが届く前も先頭の要素は必ず置く
  const setPrefectureNameInSelectedLocations: () => void = () => {
    setSelectedLocations([{ value: null, label: findPrefectureName() }]);
  };

  useEffect(() => {
    setPrefectureNameInSelectedLocations();
  }, [prefectureId]);

  // 都道府県マスタが開いた後に届いたら、先頭の都道府県名だけを埋める
  useEffect(() => {
    const prefectureName = findPrefectureName();
    if (!prefectureName) return;
    setSelectedLocations((prev) =>
      prev[0]?.value === null && prev[0].label !== prefectureName
        ? [{ value: null, label: prefectureName }, ...prev.slice(1)]
        : prev
    );
  }, [prefectures]);

  useEffect(() => {
    const isCurrent = startRequest();
    const fetchLocations: () => Promise<void> = async () => {
      setLoading(true);
      setSearchText("");
      const locations = await getSelectNextLocation(apiClient, prefectureId);
      if (!isCurrent()) return;
      if (locations.failed) toast.error(FETCH_FAILED_MESSAGE);
      setFetchLocationList(locations.list);
      setDisplayLocationList(splitData(locations.list));
      setLoading(false);
    };

    (async () => {
      if (open) {
        resultCity = "";
        resultLocationWithoutCity = "";
        setPrefectureNameInSelectedLocations();
        await fetchLocations();
      }
    })();
  }, [open]);

  useEffect(() => {
    const filteredLocations = fetchLocationList.filter((location) =>
      location.name.toLowerCase().includes(searchText.toLowerCase())
    );
    setDisplayLocationList(splitData(filteredLocations));
  }, [searchText]);

  return (
    <Modal
      open={open}
      onClose={(
        event: Record<string, unknown>,
        reason: "backdropClick" | "escapeKeyDown"
      ) => {
        // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          height: "80vh",
          width: "80vw",
          boxShadow: 24,
          outline: "none",
          border: "1px solid",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 2,
            py: 2,
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{ color: "common.white" }}
          >
            所在を選択してください
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            pt: 4,
          }}
        >
          <Typography sx={{ pl: 3 }}>
            {selectedLocations
              .map((location) => location.label)
              .filter((label) => label !== "")
              .join(" > ")}
          </Typography>

          <Box sx={{ mt: 3, px: 4 }}>
            <TextField
              label="所在検索"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              fullWidth
            />
          </Box>

          {loading ? (
            <Stack alignItems="center">
              <CircularProgress sx={{ my: "20px" }} />
            </Stack>
          ) : (
            <TableContainer component={PagePaper}>
              <Table stickyHeader>
                <TableBody>
                  {displayLocationList.map((location, locationIndex) => (
                    <TableRow key={`location-${locationIndex}`}>
                      {location.map((cell, cellIndex) => (
                        <TableCell
                          key={`cell-${locationIndex}-${cellIndex}`}
                          onClick={() => {
                            void handleClickLocationLabelInDialog(cell);
                          }}
                          sx={{
                            cursor: "pointer",
                            textAlign: "center",
                            border: "1px solid",
                            borderColor: "divider",
                            "&:hover": {
                              backgroundColor: "primary.light",
                              color: "common.white",
                            },
                          }}
                        >
                          <Typography>{cell.name}</Typography>
                        </TableCell>
                      ))}
                      {Array.from({
                        length: DISPLAY_COLUMNS_COUNT - location.length,
                      }).map((_, i) => (
                        <TableCell
                          key={`empty-${i}`}
                          sx={{
                            border: "none",
                            borderColor: "transparent",
                          }}
                        />
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <Box
          sx={{
            p: 2,
            textAlign: "right",
            borderTop: "1px solid #ddd",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => {
              void handleBack();
            }}
            variant={ButtonVariantOption.Outlined}
            color={ButtonColorOption.Primary}
            disabled={loading || selectedLocations.length < SELECT_CITY_COUNT}
            size={SizingWrapperStyle.LARGE}
          >
            戻る
          </Button>
          <Button
            onClick={handleClose}
            variant={ButtonVariantOption.Outlined}
            color={ButtonColorOption.Error}
            size={SizingWrapperStyle.LARGE}
          >
            閉じる
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
